import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Flex, Text, Link, Icon
} from 'components/ui';
import { colors, space } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import resourceWrapper from 'utils/framework/resourceWrapper';
import Actions from 'Actions';
import userUtils from 'utils/User';
import { HEADER_VALUE } from 'constants/authentication';
import { renderModal, globalModals } from 'utils/globalModals';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import bccUtils from 'utils/BCC';
import languageLocale from 'utils/LanguageLocale';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { SHIPPING_AND_HANDLING_INFO } = globalModals;
const { US_SHIPPING_INFO, CA_SHIPPING_INFO } = bccUtils.MEDIA_IDS;
const getText = resourceWrapper(languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR));

const styles = {
    infoButton: {
        appearance: 'none',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        padding: space[1],
        margin: -space[1],
        display: 'inline-flex',
        alignItems: 'center',
        color: colors.gray
    }
};

/**
 * ShippingDetailsPanel
 *
 * Shipping ETA/zip-code row and Beauty Insider messaging below the PDP
 * Fulfillment Methods radiogroup (SHP-5111). SHP-5258 swaps the BI messaging
 * for a signed-out sign-in/create-account prompt via `userUtils.isSignedIn()`.
 *
 * @param {object} props
 * @param {string} [props.etaText] - Green "Delivery by …" text (panel format, no location).
 * @param {string} [props.zipCode] - The user's preferred ZIP/postal code; renders as a link to the location modal.
 * @param {object} [props.currentSku] - The current SKU, forwarded to the location modal action.
 * @returns {JSX.Element}
 */
const ShippingDetailsPanel = ({ etaText, zipCode, currentSku }) => {
    const dispatch = useDispatch();
    const globalModalsData = useSelector(globalModalsSelector);
    const shippingAndHandlingModal = globalModalsData[SHIPPING_AND_HANDLING_INFO];
    const isSignedIn = userUtils.isSignedIn();

    const handleZipClick = useCallback(() => {
        dispatch(
            Actions.showShippingDeliveryLocationModal({
                isOpen: true,
                sduZipcodeModal: false,
                sku: currentSku
            })
        );
    }, [dispatch, currentSku]);

    // SHP-5258: signed-out clients get a sign-in/create-account prompt instead of
    // the Beauty Insider messaging, since free standard shipping is a BI benefit.
    const handleSignInClick = useCallback(
        () => dispatch(Actions.showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } })),
        [dispatch]
    );

    const handleCreateAccountClick = useCallback(() => dispatch(Actions.showRegisterModal({ isOpen: true })), [dispatch]);

    const handleInfoClick = useCallback(
        e => {
            e?.preventDefault();
            e?.stopPropagation();
            renderModal(shippingAndHandlingModal, () => {
                dispatch(
                    Actions.showMediaModal({
                        isOpen: true,
                        mediaId: languageLocale.isCanada() ? CA_SHIPPING_INFO : US_SHIPPING_INFO,
                        titleDataAt: 'shippingHandlingModalTitle'
                    })
                );
            });
        },
        [dispatch, shippingAndHandlingModal]
    );

    return (
        <Box>
            <Flex
                alignItems='center'
                justifyContent='space-between'
                width='100%'
                marginBottom={2}
            >
                {etaText && zipCode && (
                    <Text is='p'>
                        <Text
                            is='span'
                            fontSize='sm'
                            fontWeight='bold'
                            color='green'
                            children={etaText}
                        />
                        <Text
                            is='span'
                            color='black'
                            children={` ${getText('shippingPanelTo')} `}
                        />
                        <Link
                            onClick={handleZipClick}
                            arrowDirection='down'
                            fontSize='sm'
                            fontWeight='bold'
                            padding={2}
                            margin={-2}
                            aria-label={getText('shippingPanelZipAriaLabel')}
                            children={zipCode}
                        />
                    </Text>
                )}
                <button
                    type='button'
                    onClick={handleInfoClick}
                    aria-label={getText('shippingPanelInfoAriaLabel')}
                    css={styles.infoButton}
                >
                    <Icon
                        name='infoOutline'
                        size={16}
                    />
                </button>
            </Flex>
            <Text
                is='p'
                fontSize='sm'
                color='black'
                lineHeight='tight'
            >
                {isSignedIn ? (
                    <>
                        {getText('shippingPanelBiPrefix')}
                        <Text
                            is='span'
                            fontWeight='bold'
                            children={getText('shippingPanelBiSuffix')}
                        />
                    </>
                ) : (
                    getText(
                        'shippingPanelSignInPrompt',
                        false,
                        <Link
                            onClick={handleSignInClick}
                            color='blue'
                            underline={true}
                            fontSize='sm'
                            padding={2}
                            margin={-2}
                            children={getText('signInLinkLabel')}
                        />,
                        <Link
                            onClick={handleCreateAccountClick}
                            color='blue'
                            underline={true}
                            fontSize='sm'
                            padding={2}
                            margin={-2}
                            children={getText('createAccountLinkLabel')}
                        />,
                        <Text
                            is='span'
                            fontWeight='bold'
                            children={getText('shippingPanelBiSuffix')}
                        />
                    )
                )}
            </Text>
        </Box>
    );
};

ShippingDetailsPanel.propTypes = {
    etaText: PropTypes.string,
    zipCode: PropTypes.string,
    currentSku: PropTypes.object
};

export default wrapFunctionalComponent(ShippingDetailsPanel, 'ShippingDetailsPanel');
